import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import { fetchServiceConfigs, deleteServiceConfig } from '../store/slices/serviceConfigSlice';
import { addNotification, showModal } from '../store/slices/uiSlice';
import { resolveServiceId, ConfigMode } from '../api/serviceConfigApi';
import { FiPlus, FiEdit2, FiTrash2, FiEye, FiSearch } from 'react-icons/fi';

const MODE_COLORS: Record<string, string> = {
    default:     'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    development: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    staging:     'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    production:  'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    demo:        'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
    qa:          'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
    uat:         'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300',
};

const ServiceConfigsPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { configs, loading } = useSelector((state: RootState) => state.serviceConfigs);
    const [search, setSearch] = useState('');

    useEffect(() => { dispatch(fetchServiceConfigs()); }, [dispatch]);

    const filtered = configs.filter(c =>
        (resolveServiceId(c)).toLowerCase().includes(search.toLowerCase()) ||
        c.mode.toLowerCase().includes(search.toLowerCase())
    );

    // Group by service ID so each service shows as a header with its mode variants
    const grouped = filtered.reduce<Record<string, typeof filtered>>((acc, c) => {
        const key = resolveServiceId(c);
        if (!acc[key]) acc[key] = [];
        acc[key].push(c);
        return acc;
    }, {});

    const handleDelete = (id: string, label: string) => {
        dispatch(showModal({
            title: 'Delete Service Config',
            content: `Delete config "${label}"? This cannot be undone.`,
            confirmLabel: 'Delete',
            cancelLabel: 'Cancel',
            onConfirm: async () => {
                try {
                    await dispatch(deleteServiceConfig(id)).unwrap();
                    dispatch(addNotification({ type: 'success', message: `Config "${label}" deleted.` }));
                } catch (e: any) {
                    dispatch(addNotification({ type: 'error', message: e.message }));
                }
            },
        }));
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Service Configurations</h1>
                <Link to="/service-configs/create"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700">
                    <FiPlus className="mr-2" /> New Config
                </Link>
            </div>

            <p className="text-sm text-gray-500 dark:text-gray-400">
                Resolution order (lowest → highest): <code className="text-xs">global:default</code> →{' '}
                <code className="text-xs">global:{'{mode}'}</code> →{' '}
                <code className="text-xs">{'{service}'}:default</code> →{' '}
                <code className="text-xs">{'{service}'}:{'{mode}'}</code>
            </p>

            {/* Search */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <div className="relative">
                    <FiSearch className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <input type="text" placeholder="Filter by service or mode…" value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="pl-9 block w-full text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md" />
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-500" />
                </div>
            ) : Object.keys(grouped).length === 0 ? (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-8 text-center text-gray-500 dark:text-gray-400">
                    No service configs found.
                </div>
            ) : (
                <div className="space-y-4">
                    {Object.entries(grouped).sort(([a], [b]) => {
                        // global first, then alphabetical
                        if (a === 'global') return -1;
                        if (b === 'global') return 1;
                        return a.localeCompare(b);
                    }).map(([serviceId, items]) => (
                        <div key={serviceId} className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                                <span className="text-sm font-semibold text-gray-900 dark:text-white font-mono">
                                    {serviceId}
                                    {serviceId === 'global' && (
                                        <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                                            — applies to all services
                                        </span>
                                    )}
                                </span>
                            </div>
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                    {items.sort((a, b) => {
                                        const order = ['default','development','staging','production','demo','qa','uat'];
                                        return order.indexOf(a.mode) - order.indexOf(b.mode);
                                    }).map(cfg => (
                                        <tr key={cfg._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                            <td className="px-4 py-3 w-32">
                                                <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${MODE_COLORS[cfg.mode] ?? MODE_COLORS.default}`}>
                                                    {cfg.mode}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                                                {cfg.subscribedTopics?.length
                                                    ? <span>{cfg.subscribedTopics.length} topic{cfg.subscribedTopics.length !== 1 ? 's' : ''}</span>
                                                    : null}
                                                {Object.keys(cfg).filter(k => !['_id','serviceId','serviceName','mode','subscribedTopics','createdAt','updatedAt','__v'].includes(k)).map(k => (
                                                    <span key={k} className="ml-2 inline-flex px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300">
                                                        {k}
                                                    </span>
                                                ))}
                                            </td>
                                            <td className="px-4 py-3 text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
                                                {cfg.updatedAt ? new Date(cfg.updatedAt).toLocaleDateString() : ''}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="flex justify-end gap-3">
                                                    <Link to={`/service-configs/${cfg._id}`}
                                                        className="text-primary-600 hover:text-primary-900 dark:text-primary-400">
                                                        <FiEye className="h-4 w-4" />
                                                    </Link>
                                                    <Link to={`/service-configs/${cfg._id}/edit`}
                                                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400">
                                                        <FiEdit2 className="h-4 w-4" />
                                                    </Link>
                                                    <button onClick={() => handleDelete(cfg._id, `${serviceId}:${cfg.mode}`)}
                                                        className="text-red-600 hover:text-red-900 dark:text-red-400">
                                                        <FiTrash2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ServiceConfigsPage;
